import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useProfile } from '../hooks/useProfile';

interface Connection {
  id: string;
  status: string;
  requested_at: string;
  allocator_firm: {
    id: string;
    name: string;
    type: string;
    headquarters: string;
    aum_min: number;
    aum_max: number;
  };
  manager_firm: {
    id: string;
    name: string;
  };
  requested_by_profile: {
    full_name: string;
    email: string;
  };
}

const statusColors: Record<string, { color: string; bg: string }> = {
  pending: { color: '#92640A', bg: '#92640A15' },
  approved: { color: '#0A7C42', bg: '#0A7C4215' },
  declined: { color: '#C0222A', bg: '#C0222A15' },
};

export default function ManageInvestors() {
  const { firm, profile } = useProfile();
  const navigate = useNavigate();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  const isAllocator = profile?.role === 'allocator';

  useEffect(() => {
    if (!firm) return;
    fetchConnections();
  }, [firm]);

  const fetchConnections = async () => {
    if (!firm) return;

    let query = supabase
      .from('connections')
      .select(`
        *,
        allocator_firm:firms!connections_allocator_firm_id_fkey(id, name, type, headquarters, aum_min, aum_max),
        manager_firm:firms!connections_manager_firm_id_fkey(id, name),
        requested_by_profile:profiles!connections_requested_by_fkey(full_name, email)
      `)
      .order('requested_at', { ascending: false });

    if (isAllocator) {
      query = query.eq('allocator_firm_id', firm.id);
    } else {
      query = query.eq('manager_firm_id', firm.id);
    }

    const { data } = await query;
    if (data) setConnections(data);
    setLoading(false);
  };

  const handleAction = async (id: string, status: 'approved' | 'declined') => {
    await supabase.from('connections').update({
      status,
      responded_at: new Date().toISOString(),
    }).eq('id', id);

    if (status === 'approved' && profile && firm) {
      const c = connections.find(x => x.id === id);
      const af = c ? (Array.isArray(c.allocator_firm) ? c.allocator_firm[0] : c.allocator_firm) : null;
      logActivity(profile.id, firm.id, 'connection_approved', 'connection', id, {
        allocator_firm_id: af?.id, allocator_firm_name: af?.name,
      });
    }

    try {
      const conn = connections.find(c => c.id === id);
      if (conn) {
        const allocatorEmail = conn.requested_by_profile?.email;
        const allocatorName = conn.requested_by_profile?.full_name || 'there';
        const managerFirmName = firm?.name || 'the manager';
        const mf = Array.isArray(conn.manager_firm) ? conn.manager_firm[0] : conn.manager_firm;

        await supabase.functions.invoke('send-email-notification', {
          body: {
            type: status === 'approved' ? 'access_approved' : 'access_declined',
            data: {
              allocator_email: allocatorEmail,
              allocator_name: allocatorName,
              fund_name: `all funds at ${mf?.name || managerFirmName}`,
              manager_firm_name: managerFirmName,
            },
          },
        });
      }
    } catch (err) {
      console.error('Email notification failed:', err);
    }

    fetchConnections();
  };

  const filtered = connections.filter(c => c.status === activeTab);
  const counts = {
    pending: connections.filter(c => c.status === 'pending').length,
    approved: connections.filter(c => c.status === 'approved').length,
    declined: connections.filter(c => c.status === 'declined').length,
  };

  const pageTitle = isAllocator ? 'Manage Managers' : 'Manage Investors';
  const pageSubtitle = isAllocator
    ? 'Track and manage your manager connection requests'
    : 'Review and manage investor access requests';

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '26px', color: '#0A0A0A', marginBottom: '4px' }}>
          {pageTitle}
        </h1>
        <p style={{ fontSize: '13px', color: '#6B7280' }}>
          {pageSubtitle}
        </p>
      </div>

      <div style={{ display: 'flex', gap: '2px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '4px', width: 'fit-content', marginBottom: '24px' }}>
        {(['pending', 'approved', 'declined'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '6px 16px', borderRadius: '3px',
            fontSize: '13px', cursor: 'pointer', border: 'none',
            background: activeTab === tab ? '#F5F6F8' : 'transparent',
            color: activeTab === tab ? '#0A0A0A' : '#6B7280',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontWeight: activeTab === tab ? 500 : 400,
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <span style={{ textTransform: 'capitalize' }}>{tab}</span>
            <span style={{
              fontSize: '10px', padding: '1px 6px', borderRadius: '20px',
              background: statusColors[tab].bg, color: statusColors[tab].color
            }}>{counts[tab]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: '#FFFFFF', border: '1px solid #D0D5DD',
          borderRadius: '4px', padding: '40px', textAlign: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#6B7280' }}>No {activeTab} {isAllocator ? 'connections' : 'requests'}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(conn => {
            const af = Array.isArray(conn.allocator_firm) ? conn.allocator_firm[0] : conn.allocator_firm;
            const mf = Array.isArray(conn.manager_firm) ? conn.manager_firm[0] : conn.manager_firm;

            return (
              <div key={conn.id} style={{
                background: '#FFFFFF', border: '1px solid #D0D5DD',
                borderRadius: '4px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                display: 'flex', alignItems: 'center', gap: '16px'
              }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '4px',
                  background: '#F5F6F8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', fontWeight: 600, color: '#0A0A0A', flexShrink: 0
                }}>
                  {isAllocator ? mf?.name?.charAt(0) : af?.name?.charAt(0) || '?'}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    onClick={() => !isAllocator && navigate(`/allocator-profile/${conn.id}`)}
                    style={{
                      fontSize: '14px', fontWeight: 500,
                      color: isAllocator ? '#0A0A0A' : '#C9A84C',
                      marginBottom: '2px',
                      cursor: isAllocator ? 'default' : 'pointer',
                    }}
                  >
                    {isAllocator ? mf?.name : af?.name || 'Unknown firm'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
                    {conn.requested_by_profile?.full_name}
                    {!isAllocator && af?.headquarters ? ` · ${af.headquarters}` : ''}
                    {!isAllocator && af?.type ? ` · ${af.type.replace('_', ' ')}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span style={{ fontSize: '11.5px', color: '#6B7280' }}>
                      Access: <span style={{ color: '#0A0A0A' }}>All funds at {isAllocator ? mf?.name : af?.name}</span>
                    </span>
                    {!isAllocator && af?.aum_min && (
                      <span style={{ fontSize: '11.5px', color: '#6B7280' }}>
                        AUM: <span style={{ color: '#0A0A0A' }}>${af.aum_min}–{af.aum_max}mm</span>
                      </span>
                    )}
                    <span style={{ fontSize: '11.5px', color: '#6B7280' }}>
                      Requested: <span style={{ color: '#0A0A0A' }}>{new Date(conn.requested_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </span>
                  </div>
                </div>

                {activeTab === 'pending' && !isAllocator ? (
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={() => navigate(`/allocator-profile/${conn.id}`)}
                      style={{
                        padding: '7px 16px', borderRadius: '3px',
                        fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                        border: '1px solid #D0D5DD',
                        background: '#FFFFFF', color: '#3D4552',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}>View profile</button>
                    <button onClick={() => handleAction(conn.id, 'approved')} style={{
                      padding: '7px 16px', borderRadius: '3px',
                      fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      border: '1px solid #0A7C4240',
                      background: '#0A7C4215', color: '#0A7C42',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>Approve</button>
                    <button onClick={() => handleAction(conn.id, 'declined')} style={{
                      padding: '7px 16px', borderRadius: '3px',
                      fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      border: '1px solid #C0222A40',
                      background: '#C0222A15', color: '#C0222A',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>Decline</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    {!isAllocator && (
                      <button
                        onClick={() => navigate(`/allocator-profile/${conn.id}`)}
                        style={{
                          padding: '7px 16px', borderRadius: '3px',
                          fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                          border: '1px solid #D0D5DD',
                          background: '#FFFFFF', color: '#3D4552',
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}>View profile</button>
                    )}
                    <span style={{
                      fontSize: '11px', fontWeight: 500, padding: '4px 10px',
                      borderRadius: '20px',
                      background: statusColors[conn.status]?.bg,
                      color: statusColors[conn.status]?.color,
                      textTransform: 'capitalize'
                    }}>{conn.status}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}